/**
 * Worker 池（DESIGN.md §4.6）。
 *
 * 计算密集任务（wasm 滤镜、编解码、PSD 解析）都从这里派发。设计约束有三条：
 *
 * 1. **池大小 `max(1, hardwareConcurrency - 2)`**：留两个核给主线程与渲染 Worker，
 *    否则拖动滑块时 UI 会和计算抢 CPU。
 * 2. **一切提交都接受 `AbortSignal`**：§4.6 要求耗时 > 16ms 的操作必须可取消。
 * 3. **取消必须真的让 Worker 停下**：wasm 计算是一段同步长循环，不会回到事件循环
 *    去查看"请停下"的消息。唯一可靠的手段是 `terminate()` 掉那个 Worker 再补一个新的。
 *    这也是本模块唯一会主动销毁 Worker 的地方。
 */

/** 池所需的最小 Worker 接口。用接口而非 `Worker` 具体类型，测试才能注入替身。 */
export interface WorkerLike {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  terminate(): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  addEventListener(type: 'error', listener: (event: unknown) => void): void;
  removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  removeEventListener(type: 'error', listener: (event: unknown) => void): void;
}

/** 派发给 Worker 的请求封套。`id` 用于把响应对回请求。 */
export interface PoolRequest {
  readonly id: number;
  readonly payload: unknown;
}

/** Worker 回传的响应封套。 */
export interface PoolResponse {
  readonly id: number;
  readonly ok: boolean;
  readonly result?: unknown;
  readonly error?: string;
}

export interface WorkerPoolOptions {
  /** 创建一个新 Worker。取消任务后需要补位时会再次调用。 */
  createWorker(): WorkerLike;
  /**
   * 池大小。省略时取 `max(1, hardwareConcurrency - 2)`。
   * 传入值会被夹到 `>= 1`：0 个 Worker 的池只会让任务永远排队。
   */
  size?: number;
  /** 覆盖 `navigator.hardwareConcurrency` 的读取，仅用于测试。 */
  hardwareConcurrency?: number;
}

export interface SubmitOptions {
  /** 取消信号。中止后任务被拒绝，且若已在执行则对应 Worker 被终止。 */
  signal?: AbortSignal;
  /** 随消息转移所有权的对象（transferable ArrayBuffer 等）。 */
  transfer?: Transferable[];
}

/**
 * 计算池大小：`max(1, hardwareConcurrency - 2)`。
 *
 * 留两个核的理由见模块注释。`hardwareConcurrency` 不可读时保守取 1——
 * 宁可串行也不要在单核设备上开出一堆互相抢占的 Worker。
 */
export function defaultPoolSize(hardwareConcurrency?: number): number {
  const cores =
    hardwareConcurrency ??
    (globalThis.navigator as { hardwareConcurrency?: number } | undefined)?.hardwareConcurrency;
  if (typeof cores !== 'number' || !Number.isFinite(cores) || cores < 1) return 1;
  return Math.max(1, Math.floor(cores) - 2);
}

/** 任务被 `AbortSignal` 中止时抛出。与 DOM 的 `AbortError` 同名，便于统一处理。 */
export class AbortError extends Error {
  override readonly name = 'AbortError';
  constructor(message = '任务已被取消。') {
    super(message);
  }
}

interface PendingTask {
  readonly id: number;
  readonly payload: unknown;
  readonly transfer: Transferable[] | undefined;
  readonly resolve: (value: unknown) => void;
  readonly reject: (reason: unknown) => void;
  /** 解除 abort 监听。任务结束（无论何种方式）都必须调用，否则 signal 泄漏监听器。 */
  detach: () => void;
}

/** 一个 Worker 及其当前承载的任务。 */
interface Slot {
  worker: WorkerLike;
  task: PendingTask | undefined;
  onMessage: (event: { data: unknown }) => void;
  onError: (event: unknown) => void;
}

export class WorkerPool {
  readonly size: number;
  readonly #createWorker: () => WorkerLike;
  readonly #slots: Slot[] = [];
  readonly #queue: PendingTask[] = [];
  #nextId = 1;
  #disposed = false;

  constructor(options: WorkerPoolOptions) {
    this.#createWorker = options.createWorker;
    this.size = Math.max(1, Math.floor(options.size ?? defaultPoolSize(options.hardwareConcurrency)));
    for (let i = 0; i < this.size; i++) this.#slots.push(this.#spawn());
  }

  /** 当前有多少任务正在 Worker 上执行。 */
  get activeCount(): number {
    return this.#slots.reduce((n, s) => n + (s.task === undefined ? 0 : 1), 0);
  }

  /** 当前有多少任务在排队。 */
  get queuedCount(): number {
    return this.#queue.length;
  }

  #spawn(): Slot {
    const worker = this.#createWorker();
    const slot: Slot = {
      worker,
      task: undefined,
      onMessage: (event) => {
        this.#onMessage(slot, event.data);
      },
      onError: (event) => {
        this.#onError(slot, event);
      },
    };
    worker.addEventListener('message', slot.onMessage);
    worker.addEventListener('error', slot.onError);
    return slot;
  }

  #onMessage(slot: Slot, data: unknown): void {
    const task = slot.task;
    if (task === undefined) return;
    const response = data as Partial<PoolResponse> | null;
    // 丢弃串台的响应：terminate 后补位的 Worker 可能收到上一任务的残留消息。
    if (response === null || typeof response !== 'object' || response.id !== task.id) return;

    slot.task = undefined;
    task.detach();
    if (response.ok === true) task.resolve(response.result);
    else task.reject(new Error(typeof response.error === 'string' ? response.error : 'Worker 任务失败。'));
    this.#pump();
  }

  #onError(slot: Slot, event: unknown): void {
    const task = slot.task;
    // Worker 抛出未捕获错误后状态不可信，直接换一个新的。
    this.#replace(slot);
    if (task !== undefined) {
      task.detach();
      task.reject(event instanceof Error ? event : new Error('Worker 发生未捕获错误。'));
    }
    this.#pump();
  }

  /** 终止某个槽位的 Worker 并补一个新的，保持池大小恒定。 */
  #replace(slot: Slot): void {
    slot.worker.removeEventListener('message', slot.onMessage);
    slot.worker.removeEventListener('error', slot.onError);
    slot.worker.terminate();
    slot.task = undefined;

    if (this.#disposed) return;
    const fresh = this.#createWorker();
    slot.worker = fresh;
    fresh.addEventListener('message', slot.onMessage);
    fresh.addEventListener('error', slot.onError);
  }

  /**
   * 提交一个任务。
   *
   * @param payload 传给 Worker 的数据，必须可结构化克隆。
   * @returns Worker 回传的结果。被取消时 reject {@link AbortError}。
   */
  submit<T = unknown>(payload: unknown, options: SubmitOptions = {}): Promise<T> {
    const { signal, transfer } = options;

    if (this.#disposed) return Promise.reject(new Error('Worker 池已释放。'));
    if (signal?.aborted === true) return Promise.reject(new AbortError());

    return new Promise<T>((resolve, reject) => {
      const task: PendingTask = {
        id: this.#nextId++,
        payload,
        transfer,
        resolve: resolve as (value: unknown) => void,
        reject,
        detach: () => {},
      };

      if (signal !== undefined) {
        const onAbort = (): void => {
          this.#abort(task);
        };
        signal.addEventListener('abort', onAbort, { once: true });
        task.detach = () => {
          signal.removeEventListener('abort', onAbort);
        };
      }

      this.#queue.push(task);
      this.#pump();
    });
  }

  /**
   * 中止一个任务。
   *
   * 排队中的直接摘除；已在执行的必须连 Worker 一起终止——同步的 wasm 循环
   * 不会理会任何"请停下"的消息，只有终止线程才能真的把 CPU 还回来。
   */
  #abort(task: PendingTask): void {
    const queueIndex = this.#queue.indexOf(task);
    if (queueIndex >= 0) {
      this.#queue.splice(queueIndex, 1);
      task.detach();
      task.reject(new AbortError());
      return;
    }

    const slot = this.#slots.find((s) => s.task === task);
    if (slot !== undefined) {
      this.#replace(slot);
      task.detach();
      task.reject(new AbortError());
      this.#pump();
    }
  }

  /** 把排队的任务填进空闲槽位。 */
  #pump(): void {
    if (this.#disposed) return;
    for (const slot of this.#slots) {
      if (this.#queue.length === 0) return;
      if (slot.task !== undefined) continue;
      const task = this.#queue.shift();
      if (task === undefined) return;
      slot.task = task;
      const request: PoolRequest = { id: task.id, payload: task.payload };
      slot.worker.postMessage(request, task.transfer);
    }
  }

  /** 终止全部 Worker 并拒绝所有未完成任务。释放后不可再用。 */
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;

    for (const slot of this.#slots) {
      const task = slot.task;
      slot.worker.removeEventListener('message', slot.onMessage);
      slot.worker.removeEventListener('error', slot.onError);
      slot.worker.terminate();
      slot.task = undefined;
      if (task !== undefined) {
        task.detach();
        task.reject(new AbortError('Worker 池已释放。'));
      }
    }
    while (this.#queue.length > 0) {
      const task = this.#queue.shift();
      if (task === undefined) break;
      task.detach();
      task.reject(new AbortError('Worker 池已释放。'));
    }
  }
}
